import { z } from 'zod';
import { apiFetch } from './api';

/**
 * Request wrapper + Zod schema for the reverse, human-initiated Saleshandy
 * linking flow — `POST /saleshandy/link/verify`
 * (`apps/api/src/modules/saleshandy/controllers/saleshandy-link-verification.controller.ts`).
 * An already-logged-in TI org OWNER pastes their own Saleshandy API key
 * instead of waiting for Saleshandy to push the link. Mirrors the shape of
 * `lib/api-keys.ts`: one file per backend resource, exporting a typed request
 * function consumed by `features/settings/queries.ts`.
 *
 * `organizationId`/`workspaceId` in the response are always the CALLER's own
 * (JWT-derived) ids — never a new org, unlike the push `/accounts/link`
 * flow this mirrors on the server side.
 */

const saleshandySyncResultSchema = z.object({
  status: z.enum(['synced', 'skipped']),
  reason: z.enum(['not_consented', 'paddle_managed', 'up_to_date', 'migration_v1_owned', 'plan_not_mapped']).optional(),
  planId: z.number().optional(),
});

export const saleshandyRetroactiveLinkSchema = z.object({
  organizationId: z.number(),
  workspaceId: z.number(),
  userId: z.number(),
  sync: saleshandySyncResultSchema,
  // Saleshandy's consent page. The link is created UNCONSENTED (and the org
  // reads as not linked) until the owner confirms there; null when Saleshandy
  // returned no usable URL.
  consentUrl: z.string().nullable(),
});

export type SaleshandyRetroactiveLinkResult = z.infer<typeof saleshandyRetroactiveLinkSchema>;

export async function linkSaleshandyAccount(apiKey: string): Promise<SaleshandyRetroactiveLinkResult> {
  const data = await apiFetch<unknown>('/saleshandy/link/verify', {
    method: 'POST',
    body: JSON.stringify({ apiKey }),
  });
  return saleshandyRetroactiveLinkSchema.parse(data);
}

/** `GET /saleshandy/link/status` — a pending (unconsented) link reads `linked: false`. */
export const saleshandyLinkStatusSchema = z.object({
  linked: z.boolean(),
  shAccountId: z.string().nullable(),
  clientName: z.string().nullable(),
});

export type SaleshandyLinkStatus = z.infer<typeof saleshandyLinkStatusSchema>;

export async function getSaleshandyLinkStatus(): Promise<SaleshandyLinkStatus> {
  const data = await apiFetch<unknown>('/saleshandy/link/status');
  return saleshandyLinkStatusSchema.parse(data);
}
